import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Webhook,
  Plus,
  RefreshCw,
  ExternalLink,
  Copy,
  AlertCircle,
  Loader2,
  Settings,
  CreditCard,
  ShoppingCart,
  History,
  CheckCircle,
  XCircle,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';
import api from '../lib/api';

// Import extracted components
import { 
  StatusBadge, 
  WebhookCard, 
  DeliveryRow,
  PAYMENT_PLATFORMS, 
  PLAN_TYPES, 
  WEBHOOK_EVENTS 
} from '../components/webhooks';

export default function Webhooks() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [activeTab, setActiveTab] = useState('outbound');
  
  // Payment webhooks state
  const [paymentConfigs, setPaymentConfigs] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [showPaymentConfig, setShowPaymentConfig] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    platform: '',
    name: '',
    secret_key: '',
    is_active: true,
    product_mappings: {}
  });
  const [newProductMapping, setNewProductMapping] = useState({ product_id: '', plan: 'individual' });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    is_active: true
  });
  
  const fetchWebhooks = useCallback(async () => {
    try {
      const [webhooksRes, eventsRes] = await Promise.all([
        api.get('/webhooks'),
        api.get('/webhooks/events')
      ]);
      setWebhooks(webhooksRes.data.webhooks || []);
      setAvailableEvents(eventsRes.data.events || WEBHOOK_EVENTS);
    } catch (error) {
      toast.error('Erro ao carregar webhooks');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);
  
  // Payment webhooks functions
  const fetchPaymentConfigs = useCallback(async () => {
    if (!user?.is_developer && !user?.is_admin) return;
    
    setLoadingPayment(true);
    try {
      const [configsRes, logsRes] = await Promise.all([
        api.get('/payment-webhooks/configs'),
        api.get('/payment-webhooks/logs?limit=50')
      ]);
      setPaymentConfigs(configsRes.data.configs || []);
      setPaymentLogs(logsRes.data.logs || []);
    } catch (error) {
      logger.error('Failed to load payment configs:', error);
    } finally {
      setLoadingPayment(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (activeTab === 'payment') {
      fetchPaymentConfigs();
    }
  }, [activeTab, fetchPaymentConfigs]);
  
  const handleSavePaymentConfig = async () => {
    if (!paymentFormData.platform) {
      toast.error('Selecione uma plataforma');
      return;
    }
    
    try {
      await api.post('/payment-webhooks/configs', paymentFormData);
      toast.success('Configuração salva!');
      setShowPaymentConfig(false);
      setPaymentFormData({ platform: '', name: '', secret_key: '', is_active: true, product_mappings: {} });
      fetchPaymentConfigs();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };
  
  const addProductMapping = () => {
    if (!newProductMapping.product_id) return;
    
    setPaymentFormData(prev => ({
      ...prev,
      product_mappings: {
        ...prev.product_mappings,
        [newProductMapping.product_id]: newProductMapping.plan
      }
    }));
    setNewProductMapping({ product_id: '', plan: 'individual' });
  };
  
  const removeProductMapping = (productId) => {
    setPaymentFormData(prev => {
      const mappings = { ...prev.product_mappings };
      delete mappings[productId];
      return { ...prev, product_mappings: mappings };
    });
  };
  
  const copyWebhookUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiada!');
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.url || formData.events.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      await api.post('/webhooks', formData);
      toast.success('Webhook criado!');
      setShowCreate(false);
      setFormData({ name: '', url: '', events: [], is_active: true });
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar webhook');
    }
  };
  
  const handleUpdate = async () => {
    if (!editingWebhook) return;
    
    try {
      await api.patch(`/webhooks/${editingWebhook.id}`, formData);
      toast.success('Webhook atualizado!');
      setEditingWebhook(null);
      setFormData({ name: '', url: '', events: [], is_active: true });
      fetchWebhooks();
    } catch (error) {
      toast.error('Erro ao atualizar webhook');
    }
  };
  
  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este webhook?')) return;
    
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success('Webhook excluído');
      fetchWebhooks();
    } catch (error) {
      toast.error('Erro ao excluir webhook');
    }
  };
  
  const handleTest = async (id) => {
    try {
      const res = await api.post(`/webhooks/${id}/test`);
      if (res.data.status === 'success') {
        toast.success('Teste enviado com sucesso!');
      } else {
        toast.error(`Teste falhou: ${res.data.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      toast.error('Erro ao testar webhook');
    }
  };
  
  const handleViewHistory = async (id) => {
    setSelectedWebhookId(id);
    setShowHistory(true);
    setLoadingDeliveries(true);
    
    try {
      const res = await api.get(`/webhooks/${id}/deliveries`);
      setDeliveries(res.data.deliveries || []);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingDeliveries(false);
    }
  };
  
  const handleEdit = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      is_active: webhook.is_active
    });
  };
  
  const toggleEvent = (eventValue) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter(e => e !== eventValue)
        : [...prev.events, eventValue]
    }));
  };
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }
  
  // Group events by category
  const eventsByCategory = WEBHOOK_EVENTS.reduce((acc, event) => {
    if (!acc[event.category]) acc[event.category] = [];
    acc[event.category].push(event);
    return acc;
  }, {});
  
  return (
    <div className="p-6 space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Webhook className="w-7 h-7 text-primary" />
            Webhooks
          </h1>
          <p className="text-muted-foreground">
            Integre com Zapier, Make, Hotmart, Kiwify e mais
          </p>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="outbound" className="flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            Outbound
          </TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Pagamentos
          </TabsTrigger>
        </TabsList>
        
        {/* Outbound Webhooks Tab */}
        <TabsContent value="outbound" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreate(true)} data-testid="create-webhook-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Webhook
            </Button>
          </div>
          
          {/* Info Banner */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Automatize seu workflow</p>
                <p className="text-sm text-muted-foreground">
                  Webhooks enviam eventos em tempo real para suas automações. 
                  Use com Zapier, Make, n8n ou qualquer URL que aceite POST requests.
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Webhooks Grid */}
          {webhooks.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <Webhook className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum Webhook</h3>
                <p className="text-muted-foreground mb-4">
                  Crie seu primeiro webhook para começar a receber eventos
                </p>
                <Button onClick={() => setShowCreate(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Webhook
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {webhooks.map(webhook => (
                <WebhookCard
                  key={webhook.id}
                  webhook={webhook}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onTest={handleTest}
                  onViewHistory={handleViewHistory}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Payment Webhooks Tab */}
        <TabsContent value="payment" className="space-y-6">
          {/* Payment Info Banner */}
          <Card className="bg-green-500/10 border-green-500/20">
            <CardContent className="py-4 flex items-start gap-3">
              <ShoppingCart className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <p className="font-medium text-green-400">Webhooks de Pagamento</p>
                <p className="text-sm text-muted-foreground">
                  Configure webhooks para liberar acesso automaticamente quando um cliente comprar 
                  seu produto em plataformas como Hotmart, Kiwify, Eduzz e outras.
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Platform Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAYMENT_PLATFORMS.map(platform => {
              const config = paymentConfigs.find(c => c.platform === platform.value);
              return (
                <Card key={platform.value} className="glass-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${platform.color}`} />
                        <CardTitle className="text-lg">{platform.label}</CardTitle>
                      </div>
                      {config?.is_active && (
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                          Ativo
                        </Badge>
                      )}
                    </div>
                    <CardDescription>{platform.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">URL do Webhook</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                          {platform.webhookUrl}
                        </code>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => copyWebhookUrl(platform.webhookUrl)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {config && Object.keys(config.product_mappings || {}).length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Mapeamentos</Label>
                        <div className="space-y-1 mt-1">
                          {Object.entries(config.product_mappings).map(([prodId, plan]) => (
                            <div key={prodId} className="text-xs flex justify-between">
                              <span className="text-muted-foreground">{prodId}</span>
                              <Badge variant="secondary" className="text-xs">{plan}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setPaymentFormData({
                          platform: platform.value,
                          name: `${platform.label} Webhook`,
                          secret_key: config?.secret_key || '',
                          is_active: config?.is_active ?? true,
                          product_mappings: config?.product_mappings || {}
                        });
                        setShowPaymentConfig(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {config ? 'Configurar' : 'Ativar'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Payment Logs */}
          {paymentLogs.length > 0 && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Logs Recentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {paymentLogs.slice(0, 10).map((log, i) => (
                    <div 
                      key={`log-${log.timestamp}-${i}`}
                      className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm"
                    >
                      <div className="flex items-center gap-2">
                        {log.success ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                        <span>{log.customer_email}</span>
                        <Badge variant="secondary" className="text-xs">{log.platform}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{log.plan_type}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showCreate || !!editingWebhook} onOpenChange={(open) => {
        if (!open) {
          setShowCreate(false);
          setEditingWebhook(null);
          setFormData({ name: '', url: '', events: [], is_active: true });
        }
      }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWebhook ? 'Editar Webhook' : 'Criar Webhook'}
            </DialogTitle>
            <DialogDescription>
              Configure a URL e os eventos que você quer receber
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                placeholder="Meu Webhook"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                placeholder="https://hooks.zapier.com/..."
                value={formData.url}
                onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Eventos</Label>
              <div className="mt-2 space-y-4">
                {Object.entries(eventsByCategory).map(([category, events]) => (
                  <div key={category}>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{category}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {events.map(event => (
                        <div key={event.value} className="flex items-center gap-2">
                          <Checkbox
                            id={event.value}
                            checked={formData.events.includes(event.value)}
                            onCheckedChange={() => toggleEvent(event.value)}
                          />
                          <label htmlFor={event.value} className="text-sm cursor-pointer">
                            {event.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="active">Ativo</Label>
              <Switch
                id="active"
                checked={formData.is_active}
                onCheckedChange={checked => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreate(false);
              setEditingWebhook(null);
            }}>
              Cancelar
            </Button>
            <Button onClick={editingWebhook ? handleUpdate : handleCreate}>
              {editingWebhook ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Entregas
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 max-h-96 overflow-y-auto">
            {loadingDeliveries ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : deliveries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Nenhuma entrega ainda</p>
              </div>
            ) : (
              deliveries.map(delivery => (
                <DeliveryRow key={delivery.id} delivery={delivery} />
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Payment Config Dialog */}
      <Dialog open={showPaymentConfig} onOpenChange={setShowPaymentConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Configurar Webhook de Pagamento
            </DialogTitle>
            <DialogDescription>
              Configure a integração com a plataforma de pagamento para liberar acesso automaticamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Plataforma</Label>
              <Select 
                value={paymentFormData.platform} 
                onValueChange={v => setPaymentFormData(prev => ({ ...prev, platform: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a plataforma" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${p.color}`} />
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Chave Secreta (Webhook Secret)</Label>
              <Input
                type="password"
                placeholder="Opcional - para validação de assinatura"
                value={paymentFormData.secret_key}
                onChange={e => setPaymentFormData(prev => ({ ...prev, secret_key: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Encontre nas configurações de webhook da plataforma
              </p>
            </div>
            
            <div>
              <Label>Mapeamento de Produtos</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Associe IDs de produtos aos planos do Viral Clipes
              </p>
              
              {/* Existing mappings */}
              {Object.entries(paymentFormData.product_mappings).length > 0 && (
                <div className="space-y-2 mb-3">
                  {Object.entries(paymentFormData.product_mappings).map(([prodId, plan]) => (
                    <div key={prodId} className="flex items-center justify-between bg-muted/30 px-3 py-2 rounded">
                      <div>
                        <span className="text-sm">{prodId}</span>
                        <Badge variant="secondary" className="ml-2">{plan}</Badge>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => removeProductMapping(prodId)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new mapping */}
              <div className="flex gap-2">
                <Input
                  placeholder="ID do Produto"
                  value={newProductMapping.product_id}
                  onChange={e => setNewProductMapping(prev => ({ ...prev, product_id: e.target.value }))}
                  className="flex-1"
                />
                <Select
                  value={newProductMapping.plan}
                  onValueChange={v => setNewProductMapping(prev => ({ ...prev, plan: v }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_TYPES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addProductMapping} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Ativo</Label>
              <Switch
                checked={paymentFormData.is_active}
                onCheckedChange={checked => setPaymentFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentConfig(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePaymentConfig}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
